import React from 'react';
import { Card } from '@os/components/ui/card';
import { Button } from '@os/components/ui/button';
import { Separator } from '@os/components/ui/separator';
import { Badge } from '@os/components/ui/badge';
import { Check, X, FileText, ListChecks } from 'lucide-react';

type ChangeItem = {
  id: string;
  type: 'insert' | 'delete';
  text: string;
  context?: string;
  at: string; // ISO timestamp
};

type Props = {
  open: boolean;
  onClose: () => void;
  changes: ChangeItem[];
  onAccept: (id: string) => void;
  onReject: (id: string) => void;
  onAcceptAll: () => void;
  onRejectAll: () => void;
};

const MapleWordReviewPanel: React.FC<Props> = ({
  open,
  onClose,
  changes,
  onAccept,
  onReject,
  onAcceptAll,
  onRejectAll
}) => {
  if (!open) return null;

  return (
    <div className="h-full w-[320px] border-r border-border bg-background flex-shrink-0">
      <Card className="h-full rounded-none shadow-none border-0 flex flex-col">
        {/* Header */}
        <div className="px-4 py-3 border-b border-border flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FileText className="w-4 h-4" />
            <h3 className="text-sm font-semibold">Review Changes</h3>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-xs">
              {changes.length} change{changes.length === 1 ? '' : 's'}
            </Badge>
            <button
              type="button"
              className="inline-flex items-center justify-center rounded-md border border-border px-2 py-1 text-xs hover:bg-muted"
              onClick={onClose}
              aria-label="Close review panel"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="p-4 space-y-3 overflow-y-auto flex-1">
          {changes.length === 0 ? (
            <div className="text-xs text-muted-foreground">No staged changes yet. Insert AI or manual edits to track.</div>
          ) : (
            <div className="space-y-3">
              {changes.map((c) => (
                <div key={c.id} className="border rounded bg-muted/20 p-2">
                  <div className="flex items-center gap-2 justify-between">
                    <Badge variant={c.type === 'insert' ? 'secondary' : 'outline'} className="text-xs">
                      {c.type === 'insert' ? 'Insert' : 'Delete'}
                    </Badge>
                    <span className="text-[10px] text-muted-foreground">{new Date(c.at).toLocaleString()}</span>
                  </div>
                  {c.context && (
                    <div className="text-[11px] text-muted-foreground mt-1 truncate" title={c.context}>
                      {c.context}
                    </div>
                  )}
                  <div className="text-xs mt-2 whitespace-pre-wrap">
                    {c.type === 'insert' ? c.text : <span className="line-through">{c.text}</span>}
                  </div>
                  <div className="flex gap-2 mt-2">
                    <Button variant="default" size="sm" onClick={() => onAccept(c.id)}>
                      <Check className="w-3.5 h-3.5 mr-2" /> Accept
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => onReject(c.id)}>
                      <X className="w-3.5 h-3.5 mr-2" /> Reject
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer actions */}
        <div className="border-t border-border p-3 flex gap-2">
          <Button variant="default" size="sm" className="w-full justify-center" onClick={onAcceptAll} disabled={changes.length === 0}>
            <ListChecks className="w-4 h-4 mr-2" />
            Accept All
          </Button>
          <Button variant="outline" size="sm" className="w-full justify-center" onClick={onRejectAll} disabled={changes.length === 0}>
            <X className="w-4 h-4 mr-2" />
            Reject All
          </Button>
        </div>
      </Card>
    </div>
  );
};

export default MapleWordReviewPanel;